import React, { useCallback, useId } from 'react'
import { FloatingLabel, Form, InputGroup } from 'react-bootstrap'

interface NumberInputProps {
    label: React.ReactNode
    prefix: React.ReactNode
    suffix: React.ReactNode
    value: string
    errorMessages: Array<string>
    min?: number
    max?: number
    step?: number
    hint: React.ReactNode
    onChange: (value: string) => Promise<void>
    disabled?: boolean
}

const NumberInput: React.FC<NumberInputProps> = ({
    label,
    prefix = '',
    suffix = '',
    value,
    errorMessages,
    min,
    max,
    step,
    hint,
    onChange,
    disabled = false,
}) => {
    const id = useId()

    const handleChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            await onChange(event.target.value)
        },
        [onChange],
    )

    const control = (
        <Form.Control
            onChange={handleChange}
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            isInvalid={errorMessages.length > 0}
            disabled={disabled}
            autoComplete="off"
        />
    )

    const floatingLabel =
        prefix || suffix ? (
            <InputGroup className="mb-0">
                {prefix && <InputGroup.Text>{prefix}</InputGroup.Text>}
                <div className="form-floating flex-grow-1">
                    {control}
                    <label htmlFor={id}>{label}</label>
                </div>
                {suffix && <InputGroup.Text>{suffix}</InputGroup.Text>}
            </InputGroup>
        ) : (
            <FloatingLabel controlId={id} label={label}>
                {control}
            </FloatingLabel>
        )

    return (
        <div className="mb-3">
            {floatingLabel}
            {errorMessages.length > 0 && (
                <div className="invalid-feedback d-block">
                    {errorMessages.join(' ')}
                </div>
            )}
            {hint && <Form.Text>{hint}</Form.Text>}
        </div>
    )
}

export default NumberInput
